import { useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { TIMEZONES } from "../../utils/timezones";
import { getTimezoneOffset } from "../../utils/timezoneShift";

// A fixed UTC offset as an IANA zone: Etc/GMT-2 is UTC+2 (the sign is inverted).
export function offsetTimezoneId(offset) {
  if (!offset) return "Etc/GMT";
  return `Etc/GMT${offset > 0 ? "-" : "+"}${Math.abs(offset)}`;
}

export default function TimezonePicker({ value, onChange, isDark, channelOffset }) {
  const options = useMemo(() => {
    const label = (tz) => {
      const offset = getTimezoneOffset(tz);
      return `${tz} (UTC${offset >= 0 ? "+" : ""}${offset})`;
    };

    const list = [];
    if (channelOffset != null) {
      const id = offsetTimezoneId(channelOffset);
      list.push({
        value: id,
        label: `Channel timezone (UTC${channelOffset >= 0 ? "+" : ""}${channelOffset})`,
      });
    }

    const zones = TIMEZONES.includes(value) || list.some((o) => o.value === value) ? TIMEZONES : [value, ...TIMEZONES];
    zones.forEach((tz) => list.push({ value: tz, label: label(tz) }));
    return list;
  }, [value, channelOffset]);

  return (
    <Autocomplete
      size="small"
      disableClearable
      options={options}
      value={options.find((t) => t.value === value) || null}
      onChange={(_, val) => val && onChange(val.value)}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      sx={{ width: 260, minWidth: 220 }}
      componentsProps={{
        paper: {
          sx: {
            bgcolor: isDark ? "#1a1f2e" : "#ffffff",
            backgroundImage: "none",
            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
            "& .MuiAutocomplete-listbox": {
              bgcolor: isDark ? "#1a1f2e" : "#ffffff",
              "& .MuiAutocomplete-option": {
                color: isDark ? "#e0e0e0" : "#111827",
                '&[aria-selected="true"]': {
                  bgcolor: isDark ? "rgba(144,202,249,0.15)" : "rgba(25,118,210,0.08)",
                },
                "&.Mui-focused": {
                  bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                },
              },
            },
          },
        },
      }}
      renderInput={(params) => <TextField {...params} label="Timezone" fullWidth />}
    />
  );
}
